import * as React from "react"
import { Link } from "react-router-dom"
import { Action, mk_widget, Widget } from "widgets-for-react"
import { isLoggedOutState, LoggedOutState, LoginState } from "../state"
import { notifications } from '../notifications';
import { MinimalLoginWidgetData, update_state_with_route } from "../login";
import { LoginFlowStyle, LoginFlowClasses } from "./style.mod";
import { getClassName } from "../../utils/utils.mod";
import { pushEventToDatalayer } from '../../../utils/analytics';
import i18next = require("i18next");

function handleSubmit<a, extra_user_info>(e: React.FormEvent<HTMLFormElement>, setState: (_: Action<LoginState<a, extra_user_info>>) => void) {
  e.preventDefault();
	setState(s => isLoggedOutState(s) ? ({ ...s, status: "logged out - logging in", login_data: s.login_data }) : s);
}

export function login_form<a, extra_user_info>(
	data: MinimalLoginWidgetData<a, extra_user_info>,
	options?: { style?: LoginFlowStyle; key?: string }
) {
	return (s0: LoggedOutState, readonly: boolean, rootpath: string): Widget<Action<LoginState<a, extra_user_info>>> => {
		return mk_widget({
			run: (setState: (_: Action<LoginState<a, extra_user_info>>) => void) => {
				return (
					<section className={getClassName(options, "block_classname", "container")} key="login-widget-login-form">
						<header className={getClassName(options, "header_classname", "mb-4")}>
							<Link className="header__link" to={`${rootpath}`}>
								<h1 className={getClassName(options, "title_classname", "mb-4")}>{i18next.t(`loginflow:login.title`)}</h1>
								<svg className="header__logo" width="120" height="32" aria-hidden="true">
									<use xlinkHref="#icon--logo" />
								</svg>
							</Link>
						</header>

						<div className={getClassName(options, "inner_classname", "py-5")}>
							<form onSubmit={(e) => handleSubmit(e, setState)} className={getClassName(options, "form_classname", "")}>
								<div className={getClassName(options, "form_inner_classname", "")}>
									<div className={getClassName(options, "form_group_classname", "form-group")}>
										<label
											htmlFor="username"
											className={getClassName(options, "form_label_classname", "")}
										>
											{i18next.t(`loginflow:login.fields.username`)}
										</label>
										<input
											type="text"
											name="username"
											id="username"
											className={getClassName(options, "form_input_classname", "form-control")}
											value={s0.login_data.username}
											disabled={readonly}
											onChange={(event) => setState(s => isLoggedOutState(s) ? ({
												...s,
												status: "logged out",
												login_data: {
													...s.login_data,
													username: event.currentTarget.value
												}
											}) : s)
											}
										/>
									</div>

									<div className={getClassName(options, "form_group_classname", "form-group")}>
										<label
											htmlFor="password"
											className={getClassName(options, "form_label_classname", "")}
										>
											{i18next.t(`loginflow:login.fields.password`)}
										</label>
										<input
											type={s0.login_data.show_password ? "text" : "password"}
											name="password"
											id="password"
											className={getClassName(options, "form_input_password_classname", "form-control")}
											value={s0.login_data.password}
											disabled={readonly}
											onChange={(event) => setState(s => isLoggedOutState(s) ? ({
												...s,
												status: "logged out",
												login_data: {
													...s.login_data,
													password: event.currentTarget.value
												}
											}) : s)
											}
										/>
										{/* Password toggle */}
                    <div className="form__password-toggle">                    
                      <input
                        type="checkbox"
                        name="show_password"
                        id="show_password"
                        className={getClassName(options, "form_checkbox_classname", "form-check-input")}
                        checked={s0.login_data.show_password}
                        disabled={readonly}
                        tabIndex={-1}
                        onChange={(event) => setState(s => isLoggedOutState(s) ? ({
                          ...s,
                          status: "logged out",
                          login_data: {
                            ...s.login_data,
                            show_password: event.target.checked
                          }
                        }) : s)}
                      />
                      <label htmlFor="show_password">
                        <span className="sr-text">{i18next.t(`loginflow:login.fields.show_password_checkbox`)}</span>
                        <svg className="form__password-icon form__password-icon--on" aria-hidden="true">
                          <use xlinkHref="#icon--eye" />
                        </svg>
                        <svg className="form__password-icon form__password-icon--off" aria-hidden="true">
                          <use xlinkHref="#icon--eye-off" />
                        </svg>
                      </label>
                    </div>
									</div>

									{notifications(s0, { style: LoginFlowClasses }).run(() => s => s)}

									<div className={getClassName(options, "form_actions_classname", "")}>
										<button
											type="submit"
											className={getClassName(options, "clickable_primary_classname", "btn btn-dark")}
											disabled={readonly}
										>
											{i18next.t(`loginflow:login.clickables.submit`)}
										</button>
									</div>

									<footer className={getClassName(options, "footer_classname", "mt-4")}>
										{data.without_browserRouter
											? <button
												type="button"
												key="forgot-password"
												className={getClassName(options, "clickable_secondary_classname", "btn btn-link border-0 ml-1 p-0")}
												onClick={() => setState(update_state_with_route({ kind: "forgot password" }, data))}
											>
												{i18next.t(`loginflow:login.clickables.forgot_password`)}
											</button>
											: <Link
												to={`${rootpath}/forgot-password`}
												key="forgot-password"
												className={getClassName(options, "clickable_secondary_classname", "ml-1")}
											>
												{i18next.t(`loginflow:login.clickables.forgot_password`)}
											</Link>
										}
									</footer>
								</div>
							</form>
						</div>

						{(s0.status == "logged out - error too many attempts") && (
              <div className="form__popup" role="dialog">
								<span>{i18next.t(`loginflow:login.popup_too_many_attempts.text`)}</span>
								<svg className="popup__icon popup__icon--triangle" width="22px" height="19px" aria-hidden="true">
									<use xlinkHref="#icon--alert-triangle" />
								</svg>
								<button
									className="popup__button"
								  onClick={() => setState(s => isLoggedOutState(s) ? ({ ...s, status: "logged out" }) : s)}
								>
									<span className="sr-text">{i18next.t(`loginflow:login.popup_too_many_attempts.button`)}</span>
									<svg pointerEvents="none" className="popup__icon popup__icon--cross" width="8px" height="8px" aria-hidden="true">
										<use pointerEvents="none" xlinkHref="#icon--cross" />
									</svg>
								</button>
							</div>
						)}
					</section>
				)
			}
		})
	}
}
